import React, {useState} from 'react';
import clsx from 'clsx';
import {
    ButtonBase,
    Checkbox,
    FormControlLabel,
    IconButton,
    InputBase,
    Popover,
    Tooltip,
    Typography
} from '@material-ui/core';
import {fade, makeStyles} from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
// import RefreshIcon from '@material-ui/icons/Refresh'; // manual refresh declined
import GetAppIcon from '@material-ui/icons/GetApp';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import JString from '@easytech-international-sdn-bhd/jstring';
import {ROLE_AGENT, ROLE_HUE, ROLE_MANAGER, ROLE_PROMOTER} from './UserRolePill';

const ROLE_FILTER_ALL = 'all';

const useStyles = makeStyles((theme) => {
    const isDark = theme.palette.type === 'dark';
    const soft = fade(theme.palette.text.primary, isDark ? 0.07 : 0.05);
    const line = fade(theme.palette.text.primary, 0.12);
    return {
        // '@keyframes spin': {
        //     to: {transform: 'rotate(360deg)'}
        // },
        root: {
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            padding: theme.spacing(1.5, 2),
            marginBottom: 16,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 16,
            background: theme.palette.type === 'dark'
                ? 'linear-gradient(135deg, rgba(254,242,0,0.07) 0%, rgba(254,242,0,0.015) 48%, rgba(255,255,255,0.02) 100%)'
                : 'linear-gradient(135deg, rgba(254,242,0,0.16) 0%, rgba(254,242,0,0.04) 48%, rgba(255,255,255,0.9) 100%)'
        },
        heading: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flex: 'none',
            marginRight: 12,
            [theme.breakpoints.down('sm')]: {
                width: '100%',
                marginRight: 0
            }
        },
        iconTile: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            flex: 'none',
            borderRadius: 12,
            backgroundColor: theme.palette.primary.main,
            color: '#111',
            '& svg': {fontSize: 26}
        },
        headingText: {
            display: 'flex',
            flexDirection: 'column',
            gap: 4
        },
        title: {
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            lineHeight: 1.2
        },
        meta: {
            fontSize: 13,
            color: theme.palette.text.secondary,
            fontVariantNumeric: 'tabular-nums'
        },
        // Same stat-group pattern as the User Account (ManageUsers) page
        segment: {
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            flexShrink: 0
        },
        segmentOption: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: theme.spacing(0.5, 1.5),
            borderRadius: 6,
            fontFamily: 'inherit',
            color: theme.palette.text.primary,
            transition: 'background-color 0.2s',
            '&:hover': {
                backgroundColor: theme.palette.action.hover
            },
            '&:not(:last-child)': {
                borderRight: `1px solid ${theme.palette.divider}`
            },
            '&:focus-visible': {outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1}
        },
        segmentOn: {
            backgroundColor: theme.palette.action.selected
        },
        segmentCount: {
            fontWeight: 700,
            fontSize: '1.1rem',
            lineHeight: 1.15,
            fontVariantNumeric: 'tabular-nums',
            color: 'inherit'
        },
        segmentLabel: {
            fontSize: '0.65rem',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            color: theme.palette.text.secondary,
            lineHeight: 1.4
        },
        segmentCountManager: {
            color: isDark ? ROLE_HUE[ROLE_MANAGER] : '#C63A00'
        },
        segmentCountAgent: {
            color: isDark ? ROLE_HUE[ROLE_AGENT] : '#5C8A00'
        },
        segmentCountPromoter: {
            color: isDark ? ROLE_HUE[ROLE_PROMOTER] : '#C77700'
        },
        search: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            height: 40,
            flex: '1 1 220px',
            minWidth: 220,
            padding: '0 8px 0 12px',
            borderRadius: 10,
            boxSizing: 'border-box',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color'], {duration: 180}),
            '&:hover': {borderColor: fade(theme.palette.text.primary, 0.3)},
            '&:focus-within': {
                backgroundColor: theme.palette.background.paper,
                borderColor: theme.palette.primary.main,
                boxShadow: `0 0 0 3px ${fade(theme.palette.primary.main, 0.45)}`
            },
            [theme.breakpoints.down('xs')]: {flexBasis: '100%'}
        },
        searchIcon: {
            color: theme.palette.text.secondary,
            fontSize: 18,
            flex: 'none'
        },
        searchInput: {
            flex: 1,
            fontSize: 14
        },
        clearButton: {
            padding: 4,
            borderRadius: 6
        },
        clearIcon: {
            fontSize: 16
        },
        iconButton: {
            width: 40,
            height: 40,
            borderRadius: 10,
            border: `1px solid ${line}`,
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.background.paper,
            transition: theme.transitions.create(['background-color', 'color', 'transform'], {duration: 150}),
            '&:hover': {backgroundColor: soft, color: theme.palette.text.primary},
            '&:active': {transform: 'scale(0.94)'},
            '& .MuiIconButton-label svg': {display: 'block', fontSize: 20}
        },
        // manual refresh declined
        // spinning: {
        //     '& svg': {animation: '$spin 700ms cubic-bezier(0.22, 1, 0.36, 1)'}
        // },
        exportButton: {
            height: 40,
            padding: '0 16px 0 12px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.02em',
            textTransform: 'none',
            boxShadow: 'none',
            transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], {duration: 150}),
            '&:hover': {boxShadow: `0 6px 16px ${fade(theme.palette.primary.main, 0.45)}`},
            '&:active': {transform: 'scale(0.97)'}
        },
        exportIcon: {
            fontSize: 18
        },
        popover: {
            borderRadius: 12,
            padding: '8px 4px',
            minWidth: 200,
            border: `1px solid ${line}`,
            boxShadow: theme.palette.type === 'dark'
                ? '0 12px 32px rgba(0,0,0,0.55)'
                : '0 12px 32px rgba(15,23,42,0.16)'
        },
        popoverTitle: {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            padding: '4px 16px 6px'
        },
        columnOption: {
            display: 'flex',
            margin: 0,
            padding: '0 12px 0 4px',
            borderRadius: 8,
            '&:hover': {backgroundColor: soft}
        },
        columnLabel: {
            fontSize: 13
        }
    };
});

const usersShownText = (template, shown, total) => {
    if (!template) return `${shown} / ${total}`;
    const first = JString(template).replaceInfinity('@shown', String(shown)).str;
    return JString(first).replaceInfinity('@total', String(total)).str;
};

export default function UserTableToolbar({
    language,
    total,
    shown,
    roleFilter,
    roleCounts,
    onRoleFilterChange,
    searchValue,
    onSearchChange,
    columnOptions,
    onToggleColumn,
    // onRefresh,
    // refreshing,
    onExport,
    exportDisabled
}) {
    const classes = useStyles();
    const [columnsAnchor, setColumnsAnchor] = useState(null);

    const roleOptions = [
        {key: ROLE_FILTER_ALL, label: language.all, countClass: null},
        {key: ROLE_MANAGER, label: language.manager, countClass: classes.segmentCountManager},
        {key: ROLE_AGENT, label: language.agent, countClass: classes.segmentCountAgent},
        {key: ROLE_PROMOTER, label: language.promoter, countClass: classes.segmentCountPromoter}
    ];

    return (
        <div className={classes.root}>
            <div className={classes.heading}>
                <span className={classes.iconTile} aria-hidden="true"><AccountBoxIcon/></span>
                <div className={classes.headingText}>
                    <Typography component="h1" className={classes.title}>{language.user_settings}</Typography>
                    <Typography component="span" className={classes.meta}>{usersShownText(language.users_shown, shown, total)}</Typography>
                </div>
            </div>

            <div className={classes.segment} role="group" aria-label={language.user_role}>
                {roleOptions.map((option) => {
                    const active = roleFilter === option.key;
                    return (
                        <ButtonBase
                            key={option.key}
                            className={clsx(classes.segmentOption, active && classes.segmentOn)}
                            aria-pressed={active}
                            onClick={() => onRoleFilterChange(option.key)}
                        >
                            <span className={clsx(classes.segmentCount, option.countClass)}>
                                {roleCounts[option.key] || 0}
                            </span>
                            <span className={classes.segmentLabel}>{option.label}</span>
                        </ButtonBase>
                    );
                })}
            </div>

            <label className={classes.search}>
                <SearchIcon className={classes.searchIcon}/>
                <InputBase
                    className={classes.searchInput}
                    placeholder={language.search}
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    inputProps={{'aria-label': language.search}}
                />
                {searchValue !== '' && (
                    <IconButton size="small" className={classes.clearButton} aria-label={language.clear} onClick={() => onSearchChange('')}>
                        <CloseIcon className={classes.clearIcon}/>
                    </IconButton>
                )}
            </label>

            <Tooltip title={language.view_columns}>
                <IconButton className={classes.iconButton} aria-label={language.view_columns} onClick={(e) => setColumnsAnchor(e.currentTarget)}>
                    <ViewColumnIcon/>
                </IconButton>
            </Tooltip>
            <Popover
                open={Boolean(columnsAnchor)}
                anchorEl={columnsAnchor}
                onClose={() => setColumnsAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                classes={{paper: classes.popover}}
            >
                <Typography component="div" className={classes.popoverTitle}>{language.view_columns}</Typography>
                {columnOptions.map((col) => (
                    <FormControlLabel
                        key={col.name}
                        className={classes.columnOption}
                        classes={{label: classes.columnLabel}}
                        control={
                            <Checkbox
                                size="small"
                                color="primary"
                                checked={col.visible}
                                onChange={() => onToggleColumn(col.name)}
                            />
                        }
                        label={col.label}
                    />
                ))}
            </Popover>

            {/* Manual refresh button declined; list still reloads when the detail dialog closes.
            <Tooltip title={language.refresh}>
                <span>
                    <IconButton
                        className={clsx(classes.iconButton, refreshing && classes.spinning)}
                        aria-label={language.refresh}
                        onClick={onRefresh}
                        disabled={refreshing}
                    >
                        <RefreshIcon/>
                    </IconButton>
                </span>
            </Tooltip>
            */}

            <Tooltip title={language.export}>
                <span>
                    <IconButton
                        className={classes.iconButton}
                        aria-label={language.export}
                        onClick={onExport}
                        disabled={exportDisabled}
                    >
                        <GetAppIcon/>
                    </IconButton>
                </span>
            </Tooltip>
        </div>
    );
}

export {ROLE_FILTER_ALL};
